import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { MonthYearSelector } from '@travellegacy/nexops-design-system';
import {
  SortableTable,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import { commissionsAPI } from '@/shared/lib/api';
import {
  ServiceType,
  serviceTypeLabels,
  type CommissionRecord,
  type CommissionSummary,
} from '@/shared/types';
import { formatCurrency } from '@/shared/lib/utils';
import { Loader2, DollarSign, Calendar, ExternalLink, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface CommissionStatementProps {
  employeeId: string;
}

export function CommissionStatement({ employeeId }: CommissionStatementProps) {
  // Estado para mês/ano selecionado (padrão: mês atual)
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const queryClient = useQueryClient();

  // Buscar resumo de comissões
  const { data: summaryResponse, isLoading: isLoadingSummary } = useQuery({
    queryKey: ['commission-summary', employeeId, selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await commissionsAPI.getSummary(employeeId, selectedMonth, selectedYear);
      return response.data;
    },
  });

  // Buscar registros de comissão
  const { data: recordsResponse, isLoading: isLoadingRecords } = useQuery({
    queryKey: ['commission-records', employeeId, selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await commissionsAPI.getRecordsByEmployee(employeeId, selectedMonth, selectedYear);
      return response.data;
    },
  });

  const summary = (summaryResponse?.data || {
    employeeId: '',
    employeeName: '',
    totalCommissions: 0,
    totalRecords: 0,
    level1Amount: 0,
    level2Amount: 0,
    level3Amount: 0,
    byServiceType: [],
  }) as CommissionSummary;

  const records = (recordsResponse?.data || []) as CommissionRecord[];

  // Função para forçar atualização (invalida cache e refetch)
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalidar cache e forçar refetch de ambas as queries
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['commission-summary', employeeId, selectedMonth, selectedYear]
        }),
        queryClient.invalidateQueries({
          queryKey: ['commission-records', employeeId, selectedMonth, selectedYear]
        }),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Função para formatar o URL do Notion corretamente
  const getNotionUrl = (notionPageId: string) => {
    // Remove hífens e formata para o padrão do Notion
    const cleanId = notionPageId.replace(/-/g, '');
    return `https://www.notion.so/${cleanId}`;
  };

  if (isLoadingSummary || isLoadingRecords) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Seletor de Mês/Ano com Botão de Atualizar */}
      <div className="flex items-center justify-between gap-4">
        <MonthYearSelector
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={setSelectedMonth}
          onYearChange={setSelectedYear}
          disableFutureMonths={true}
        />

        {/* Botão de Atualizar */}
        <button
          onClick={handleRefresh}
          disabled={isRefreshing || isLoadingSummary || isLoadingRecords}
          className="flex items-center gap-2 px-3 py-2 h-9 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Sincronizar com Notion"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          <span className="text-sm font-medium">
            {isRefreshing ? 'Sincronizando...' : 'Atualizar'}
          </span>
        </button>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Comissões
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                  {formatCurrency(summary.totalCommissions)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Nível 1: {formatCurrency(summary.level1Amount)} | Nível 2: {formatCurrency(summary.level2Amount)} | Nível 3: {formatCurrency(summary.level3Amount)}
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <DollarSign className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total de Emissões
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {summary.totalRecords}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Por Tipo de Serviço */}
      {summary.byServiceType.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Comissões por Tipo de Serviço</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {summary.byServiceType.map((item) => (
                <div
                  key={item.serviceType}
                  className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3 last:border-0"
                >
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {serviceTypeLabels[item.serviceType as ServiceType]}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {item.count} emiss{item.count !== 1 ? 'ões' : 'ão'}
                    </p>
                  </div>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(item.total)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Histórico de Comissões */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Comissões</CardTitle>
        </CardHeader>
        <CardContent>
          {records.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma comissão registrada ainda
            </p>
          ) : (
            <SortableTable data={records} defaultSortKey="issueDate" defaultSortDirection="desc">
              {(sortedRecords) => (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead sortable sortKey="issueDate">Data</TableHead>
                      <TableHead sortable sortKey="serviceType">Tipo</TableHead>
                      <TableHead sortable sortKey="level">Nível</TableHead>
                      <TableHead sortable sortKey="amount" align="right">Valor</TableHead>
                      <TableHead align="center">Notion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>
                          <div className="text-sm">
                            {format(new Date(record.issueDate), "dd/MM/yyyy 'às' HH:mm", {
                              locale: ptBR,
                            })}
                          </div>
                        </TableCell>
                        <TableCell>
                          {record.serviceType === 'FLIGHT' && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                              {serviceTypeLabels[record.serviceType]}
                            </span>
                          )}
                          {record.serviceType === 'FLIGHT_MILES' && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400">
                              {serviceTypeLabels[record.serviceType]}
                            </span>
                          )}
                          {record.serviceType === 'ACCOMMODATION' && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                              {serviceTypeLabels[record.serviceType]}
                            </span>
                          )}
                          {record.serviceType === 'INSURANCE' && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">
                              {serviceTypeLabels[record.serviceType]}
                            </span>
                          )}
                          {record.serviceType === 'OTHER_SERVICE' && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/20 dark:text-rose-400">
                              {serviceTypeLabels[record.serviceType]}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {record.level === 1 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400">
                              Nível 1
                            </span>
                          )}
                          {record.level === 2 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
                              Nível 2
                            </span>
                          )}
                          {record.level === 3 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400">
                              Nível 3
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-semibold text-right">
                            {formatCurrency(record.amount)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-center">
                            <button
                              onClick={() => window.open(getNotionUrl(record.originalNotionPageId || record.notionPageId), '_blank', 'noopener,noreferrer')}
                              className="p-2 hover:bg-accent rounded-lg transition-colors"
                              title="Abrir no Notion"
                            >
                              <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </SortableTable>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
